import { z } from "zod"
import { homeOwnerFormSchema } from "@/features/homeowners/schema/home-owner.schema"
import { homeFormSchema } from "@/features/homeowners/schema/home/home.schema"
import { makeOptional } from "@/shared/lib/helpers/schema.helpers"

// Base schema combining homeowner and home data
const baseHomeownerProfileSchema = z.object({
    homeOwner: homeOwnerFormSchema,
    home: homeFormSchema,
})

// Function to create form schema with conditional required fields based on currentStep
export const createHomeownerProfileFormSchema = (currentStep?: number) => {
    // Make address optional in the base schema since it's set during account creation
    // and shouldn't be required for profile completion
    const homeSchemaWithOptionalAddress = baseHomeownerProfileSchema.shape.home.omit({ address: true }).extend({
        address: z.string().optional(), // Make address optional
    })
    
    const baseSchema = baseHomeownerProfileSchema.extend({
        home: homeSchemaWithOptionalAddress,
    })
    
    // If currentStep = 1, add refine checks for required fields
    if (currentStep === 1) {
        return baseSchema.superRefine((data, ctx) => {
            // Define required fields for profile completion
            const requiredFields = [
                { 
                    field: 'homeOwner.dateOfBirth', 
                    message: 'Birthday is required',
                    value: data.homeOwner?.dateOfBirth
                },
                { 
                    field: 'homeOwner.isSingleParent', 
                    message: 'Is the Homeowner a single parent or single grandparent with children in the home? is required',
                    value: data.homeOwner?.isSingleParent
                },
                { 
                    field: 'homeOwner.isFirstResponder', 
                    message: 'Is there a First Responder living in the home? is required',
                    value: data.homeOwner?.isFirstResponder
                },
                { 
                    field: 'homeOwner.isVeteran', 
                    message: 'Is there a Veteran living in the home? is required',
                    value: data.homeOwner?.isVeteran
                },
                { 
                    field: 'home.repairabilityInfo.residenceType', 
                    message: 'Residence Type is required',
                    value: data.home?.repairabilityInfo?.residenceType
                },
            ]
            
            requiredFields.forEach(({ field, message, value }) => {
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                    const fieldPath = field.split('.')
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message,
                        path: fieldPath,
                    })
                }
            })
        })
    }
    
    return baseSchema
}

// Default form schema (for backward compatibility)
export const homeownerProfileFormSchema = baseHomeownerProfileSchema

export type HomeownerProfileFormType = z.infer<typeof homeownerProfileFormSchema>
export type CreateHomeownerProfileFormType = z.infer<ReturnType<typeof createHomeownerProfileFormSchema>>

